import { useState, useEffect } from 'react';
import { ModalHost } from './ModalHost';
import { apiClient } from '../../core/apiClient';

interface LLMConstraints {
  allowed_actions: string[];
  prohibited_actions: string[];
  guardrails: string[];
}

interface Tool {
  name: string;
  description: string;
  parameters: Record<string, unknown>;
}

interface LLMModalProps {
  isOpen: boolean;
  onClose: () => void;
  bridgeId?: string;
}

export function LLMModal({ isOpen, onClose, bridgeId }: LLMModalProps) {
  const [activeTab, setActiveTab] = useState<'chat' | 'facts' | 'formulas'>('chat');
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [constraints, setConstraints] = useState<LLMConstraints | null>(null);
  const [, setTools] = useState<Tool[]>([]);

  useEffect(() => {
    if (isOpen) {
      loadConstraintsAndTools();
    }
  }, [isOpen]);

  const loadConstraintsAndTools = async () => {
    try {
      const [constraintsRes, toolsRes] = await Promise.all([
        apiClient.get<LLMConstraints>('/chat/constraints'),
        apiClient.get<{ tools: Tool[] }>('/chat/tools'),
      ]);
      setConstraints(constraintsRes);
      setTools(toolsRes.tools);
    } catch (err) {
      console.error('Failed to load LLM config:', err);
    }
  };

  const handleAskQuestion = async () => {
    if (!query.trim()) return;
    setIsLoading(true);
    setResponse(null);

    try {
      const result = await apiClient.post<{ explanation: string }>('/chat/explain', {
        question: query,
        bridge_id: bridgeId,
      });
      setResponse(result.explanation);
    } catch (err) {
      setResponse('Failed to get explanation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleWhatIf = async (scenario: string) => {
    if (!bridgeId) {
      setResponse('Please select a bridge first.');
      return;
    }
    setIsLoading(true);
    setResponse(null);

    try {
      const result = await apiClient.post<{ result: { explanation: string } }>('/chat/what-if', {
        bridge_id: bridgeId,
        scenario_type: scenario,
        parameters: {},
      });
      setResponse(result.result.explanation);
    } catch (err) {
      setResponse('Failed to run scenario. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalHost isOpen={isOpen} onClose={onClose} title="AI Assistant" size="xl">
      <div className="llm-modal-content">
        <div className="tabs mb-4">
          <button
            className={`tab ${activeTab === 'chat' ? 'tab--active' : ''}`}
            onClick={() => setActiveTab('chat')}
          >
            Ask Questions
          </button>
          <button
            className={`tab ${activeTab === 'facts' ? 'tab--active' : ''}`}
            onClick={() => setActiveTab('facts')}
          >
            Ice Facts & Formulas
          </button>
          <button
            className={`tab ${activeTab === 'formulas' ? 'tab--active' : ''}`}
            onClick={() => setActiveTab('formulas')}
          >
            Mitigation Strategies
          </button>
        </div>

        {activeTab === 'chat' && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold">Ask about bridge icing:</label>
              <textarea
                className="input"
                rows={3}
                placeholder="e.g., Why is this bridge at risk? What factors contribute to ice formation?"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <button
                className="btn btn--primary"
                onClick={handleAskQuestion}
                disabled={isLoading || !query.trim()}
              >
                {isLoading ? 'Thinking...' : 'Ask'}
              </button>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold">Quick Scenarios:</label>
              <div className="flex flex-wrap gap-2">
                <button
                  className="btn btn--secondary btn--sm"
                  onClick={() => handleWhatIf('temperature_drop')}
                  disabled={isLoading}
                >
                  What if temp drops 5°C?
                </button>
                <button
                  className="btn btn--secondary btn--sm"
                  onClick={() => handleWhatIf('precipitation_change')}
                  disabled={isLoading}
                >
                  What if rain starts?
                </button>
                <button
                  className="btn btn--secondary btn--sm"
                  onClick={() => handleWhatIf('wind_increase')}
                  disabled={isLoading}
                >
                  What if wind increases?
                </button>
              </div>
            </div>

            {response && (
              <div className="card mt-4">
                <h4 className="text-sm font-semibold mb-2">Response:</h4>
                <p className="text-sm whitespace-pre-wrap">{response}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'facts' && (
          <div className="flex flex-col gap-4">
            <div className="card">
              <h4 className="text-sm font-semibold mb-2">Bridge Icing Physics</h4>
              <ul className="text-sm flex flex-col gap-2">
                <li><strong>Radiative Cooling:</strong> Bridges lose heat to the sky faster than roads due to exposure on all sides.</li>
                <li><strong>Conductive Isolation:</strong> Air beneath bridges prevents heat transfer from the ground.</li>
                <li><strong>Critical Temperature:</strong> Ice forms when deck temperature falls below 0°C with moisture present.</li>
                <li><strong>Dew Point:</strong> Frost forms when deck temperature falls below dew point and freezing point.</li>
              </ul>
            </div>

            <div className="card">
              <h4 className="text-sm font-semibold mb-2">Key Formulas</h4>
              <ul className="text-sm flex flex-col gap-2">
                <li><strong>Energy Balance:</strong> Q_net = Q_rad + Q_conv + Q_cond + Q_latent</li>
                <li><strong>Radiative Loss:</strong> Q_rad = ε·σ·(T_sky⁴ - T_deck⁴)</li>
                <li><strong>Convective Transfer:</strong> Q_conv = h·(T_air - T_deck)</li>
                <li><strong>Ice Accumulation:</strong> Δice = precip_rate × time × (1 - sublimation_loss)</li>
              </ul>
            </div>

            <div className="card">
              <h4 className="text-sm font-semibold mb-2">Surface States</h4>
              <ul className="text-sm flex flex-col gap-2">
                <li><strong>DRY:</strong> No moisture, T_deck {">"} 0°C</li>
                <li><strong>WET:</strong> Moisture present, T_deck {">"} 0°C</li>
                <li><strong>FROST:</strong> Dew point reached, T_deck ≤ 0°C</li>
                <li><strong>ICE:</strong> Frozen precipitation, T_deck ≤ 0°C</li>
                <li><strong>SNOW:</strong> Accumulated snow, T_deck ≤ 0°C</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'formulas' && (
          <div className="flex flex-col gap-4">
            <div className="card">
              <h4 className="text-sm font-semibold mb-2">Anti-Icing (Preventive)</h4>
              <ul className="text-sm flex flex-col gap-2">
                <li><strong>Timing:</strong> Apply 1-2 hours before ice onset</li>
                <li><strong>Salt Brine (23%):</strong> 50 gal/lane-mile, effective to -6°C</li>
                <li><strong>Calcium Chloride:</strong> 30 gal/lane-mile, effective to -18°C</li>
                <li><strong>Best for:</strong> Frost, light freezing rain</li>
              </ul>
            </div>

            <div className="card">
              <h4 className="text-sm font-semibold mb-2">De-Icing (Reactive)</h4>
              <ul className="text-sm flex flex-col gap-2">
                <li><strong>Rock Salt:</strong> 200-300 lb/lane-mile, effective to -9°C</li>
                <li><strong>Pre-wet Salt:</strong> 150-200 lb/lane-mile, faster activation</li>
                <li><strong>Sand/Salt Mix:</strong> For traction when temps too low for melting</li>
                <li><strong>Reapplication:</strong> Every 2-4 hours during active precipitation</li>
              </ul>
            </div>

            <div className="card">
              <h4 className="text-sm font-semibold mb-2">Treatment Decision Matrix</h4>
              <table className="text-sm w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1">Condition</th>
                    <th className="text-left py-1">Treatment</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-1">Frost expected</td>
                    <td className="py-1">Salt brine, 1hr before</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-1">Freezing rain</td>
                    <td className="py-1">CaCl₂ brine + standby crew</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-1">Snow {"<"} 2"</td>
                    <td className="py-1">Plow + pre-wet salt</td>
                  </tr>
                  <tr>
                    <td className="py-1">Snow {">"} 2"</td>
                    <td className="py-1">Plow + abrasives</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {constraints && (
              <div className="card">
                <h4 className="text-sm font-semibold mb-2">AI Capabilities</h4>
                <p className="text-xs text-secondary mb-2">The AI assistant can:</p>
                <ul className="text-xs text-secondary">
                  {constraints.allowed_actions.map((action, i) => (
                    <li key={i} className="capability-item">{action}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </ModalHost>
  );
}
